import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { equitySymbolsState, equityHubSymbolsSetState } from 'states/equityhub';
import { hiroSymbolsState } from 'states/hiro';
import { CANVAS_COMPONENT_META } from 'config/canvasComponentCatalog';
import { CanvasContentId, SymbolGroup } from 'types/canvas';
import { useCanvasGroupCtx } from './CanvasGroupContext';

export interface GroupSymOption {
  symbol: string;
  name?: string;
}

const intersect = (a: Set<string>, b: Set<string>): Set<string> => {
  const [small, big] = a.size <= b.size ? [a, b] : [b, a];
  const out = new Set<string>();
  for (const s of small) {
    if (big.has(s)) {
      out.add(s);
    }
  }
  return out;
};

const union = (a: Set<string>, b: Set<string>): Set<string> => {
  const out = new Set(a);
  for (const s of b) {
    out.add(s);
  }
  return out;
};

/**
 * The symbol universe a single member component can render.
 *   - explicit `supportedSyms` whitelist wins;
 *   - `'hiro'` → the HIRO set;
 *   - otherwise the equities set (the default / superset).
 */
const memberUniverse = (
  contentId: CanvasContentId | undefined,
  hiroSet: Set<string>,
  equitySet: Set<string>,
): Set<string> | null => {
  const meta = contentId != null ? CANVAS_COMPONENT_META[contentId] : undefined;
  if (meta == null) {
    return null;
  }
  if (meta.supportedSyms != null) {
    return new Set(meta.supportedSyms);
  }
  return meta.symbolUniverse === 'hiro' ? hiroSet : equitySet;
};

/**
 * Allowed symbols for a group = the intersection of its members' universes, so
 * a HIRO-only group allows all HIRO syms and an equity-only group allows all
 * equities (instead of always the HIRO∩equities intersection). An empty group
 * allows the union of both.
 */
const computeAllowedSymbols = (
  group: SymbolGroup | null,
  contentIdByComponentId: Map<number, CanvasContentId>,
  hiroSet: Set<string>,
  equitySet: Set<string>,
): Set<string> => {
  if (group == null || group.componentIds.length === 0) {
    return union(equitySet, hiroSet);
  }
  let acc: Set<string> | null = null;
  for (const componentId of group.componentIds) {
    const universe = memberUniverse(
      contentIdByComponentId.get(componentId),
      hiroSet,
      equitySet,
    );
    if (universe == null) {
      continue;
    }
    acc = acc == null ? new Set(universe) : intersect(acc, universe);
  }
  return acc ?? union(equitySet, hiroSet);
};

/**
 * Symbols offered in a group's symbol pickers, scoped to what its member
 * components actually support (see {@link computeAllowedSymbols}). Equity names
 * are attached when known; HIRO-only specials show just the symbol.
 */
export function useGroupSymbolOptions(
  group: SymbolGroup | null,
): GroupSymOption[] {
  const equitySymbols = useRecoilValue(equitySymbolsState);
  const hiroSet = useRecoilValue(hiroSymbolsState);
  const equitySet = useRecoilValue(equityHubSymbolsSetState);
  const { contentIdByComponentId } = useCanvasGroupCtx();

  return useMemo(() => {
    const allowed = computeAllowedSymbols(
      group,
      contentIdByComponentId,
      hiroSet,
      equitySet,
    );
    const nameBySymbol = new Map(equitySymbols.map((e) => [e.symbol, e.name]));
    return Array.from(allowed)
      .sort((a, b) => a.localeCompare(b))
      .map((symbol) => ({ symbol, name: nameBySymbol.get(symbol) }));
  }, [group, contentIdByComponentId, hiroSet, equitySet, equitySymbols]);
}

/** The allowed-symbol set for a group, for O(1) membership checks. */
export function useGroupSymbolSet(group: SymbolGroup | null): Set<string> {
  const options = useGroupSymbolOptions(group);
  return useMemo(() => new Set(options.map((o) => o.symbol)), [options]);
}
